function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

/*
 * Copyright 2019 Expedia Group, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import TrackingContext from '../../context/TrackingContext';
import deepMerge from '../../utils/DeepMerge';
/**
 * A React context provider that allows nesting to generate new context that
 * builds on parent context. This component allows applications to build the
 * payload and options for events declaratively and through nesting.
 */

var TrackingProvider = /*#__PURE__*/function (_PureComponent) {
  _inheritsLoose(TrackingProvider, _PureComponent);

  function TrackingProvider(props) {
    var _this;

    _this = _PureComponent.call(this, props) || this; // Use a class property to save context data instead of using state in
    // order to prevent excessive renders when context changes. This
    // structure must mimic the one defined in TrackingContext;

    _this.trigger = function (event, payload, options) {
      if (payload === void 0) {
        payload = {};
      }

      if (options === void 0) {
        options = {};
      }

      var data = _this.TrackingContext._data;
      var name = event || data.event;
      var eventPayload = data.eventPayload ? data.eventPayload[name] : {};
      var eventOptions = data.eventOptions ? data.eventOptions[name] : {};

      if (!name) {
        throw new TypeError('event is a required parameter');
      }

      payload = deepMerge(data.payload, eventPayload, payload);
      options = deepMerge(data.options, eventOptions, options);
      return data.trigger(name, payload, options);
    };

    _this.renderProvider = function (_temp) {
      var _ref = _temp === void 0 ? {} : _temp,
          _data = _ref._data;

      var _this$props = _this.props,
          children = _this$props.children,
          trigger = _this$props.trigger;
      _this.TrackingContext._data = _this.mergeContextData(_data); // The specified trigger property takes precedence or fallback to one
      // inherited from parent context (for nesting) or default to empty function.

      _this.TrackingContext._data.trigger = trigger || _data.trigger ||
      /* istanbul ignore next */
      function () {};

      return /*#__PURE__*/React.createElement(TrackingContext.Provider, {
        value: _this.TrackingContext
      }, children);
    };

    _this.TrackingContext = {
      _data: {
        eventPayload: _this.props.eventPayload,
        eventOptions: _this.props.eventOptions,
        payload: _this.props.payload,
        options: _this.props.options,
        trigger: _this.props.trigger ||
        /* istanbul ignore next */
        function () {}
      },
      hasProvider: true,
      trigger: _this.trigger
    };
    return _this;
  }
  /**
   * Overwrites or merges the specified properties with the existing context.
   *
   * @param {Object} data
   */


  var _proto = TrackingProvider.prototype;

  _proto.mergeContextData = function mergeContextData(data) {
    if (data === void 0) {
      data = {
        eventPayload: {},
        eventFields: {},
        eventOptions: {}
      };
    }

    var _this$props2 = this.props,
        eventFields = _this$props2.eventFields,
        eventOptions = _this$props2.eventOptions,
        fields = _this$props2.fields,
        options = _this$props2.options,
        overwrite = _this$props2.overwrite;
    var _this$props3 = this.props,
        eventPayload = _this$props3.eventPayload,
        payload = _this$props3.payload;
    var newData = {}; // Prefer new data payload name, but fall back for backwards compatibility

    eventPayload = eventPayload || eventFields;
    payload = payload || fields;

    if (overwrite) {
      newData.eventPayload = eventPayload || data.eventPayload;
      newData.eventOptions = eventOptions || data.eventOptions;
      newData.payload = payload || data.payload;
      newData.options = options || data.options;
    } else {
      // Not an overwrite so merge the properties and context objects
      newData.eventPayload = deepMerge(data.eventPayload, eventPayload);
      newData.eventOptions = deepMerge(data.eventOptions, eventOptions);
      newData.payload = deepMerge(data.payload, payload);
      newData.options = deepMerge(data.options, options);
    }

    return newData;
  }
  /**
   * The exposed context trigger API. Merges the specified values with the
   * values in context and then invokes the implementation specific trigger
   * method.
   */
  ;

  _proto.render = function render() {
    return (
      /*#__PURE__*/
      // Consume the context and then generate a new context provider
      // as a merge of specified properties and existing context.
      React.createElement(TrackingContext.Consumer, null, this.renderProvider)
    );
  };

  return TrackingProvider;
}(PureComponent);

TrackingProvider.propTypes = {
  /** (Deprecated) An object of event specific fields where the event name is the key and the value is an object of field key/value pairs for the event. Event specific values will be merged with defaults from the `fields` property. The `eventPayload` property takes precedence over this property if both are specified. */
  eventFields: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),

  /** An object of event specific payloads where the event name is the key and the value is an object of key/value pairs for the event. Event specific values will be merged with defaults from the `payload` property. */
  eventPayload: PropTypes.objectOf(PropTypes.objectOf(PropTypes.any)),

  /** An object of event specific options where the event name is the key and the value is an object of option key/value pairs for the event. Event specific values will be merged with defaults from the `options` property. */
  eventOptions: PropTypes.objectOf(PropTypes.objectOf(PropTypes.any)),

  /** (Deprecated) Object of string values that represents the default payload to apply to all events within this context. The `payload` property takes precedence over this property if both specified. */
  fields: PropTypes.objectOf(PropTypes.string),

  /** Object of values that represents the default payload to apply to all events within this context. */
  payload: PropTypes.objectOf(PropTypes.any),

  /** The trigger options. */
  options: PropTypes.objectOf(PropTypes.any),

  /** When true, overwrites the current context with specified properties. Default is to merge instead of overwrite. */
  overwrite: PropTypes.bool,

  /** Tracking event trigger implementation. */
  trigger: PropTypes.func
};
TrackingProvider.defaultProps = {
  overwrite: false
};
export default TrackingProvider;
//# sourceMappingURL=TrackingProvider.js.map